import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Brain } from 'lucide-react';
import AddLLMConfigModal from './llm/AddLLMConfigModal';
import { llmConfigurationService, type LLMConfiguration } from '../../../components/api/llmConfigurationService';
import { apiService } from '../../../components/api/apiService';
import PermissionsAssignmentModal from '../common/Permissions/PermissionsAssignmentModal';
import VaultEntityCard from './DataVault/VaultEntityCard';

const LLMs = ({ isLight = false, requestAdd = 0 }: { isLight?: boolean; requestAdd?: number }) => {
  const [showModal, setShowModal] = useState(false);
  const [editConfig, setEditConfig] = useState<LLMConfiguration | null>(null);
  const [llmsList, setLlmsList] = useState<LLMConfiguration[]>([]);
  const [providerNames, setProviderNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toggleStatusConfig, setToggleStatusConfig] = useState<LLMConfiguration | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showAssignmentModal, setAssignmentModal] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const list = await llmConfigurationService.getList();
      setLlmsList(Array.isArray(list) ? list : []);
    } catch (err) {
      setListError('Failed to load LLM configurations.');
      setLlmsList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const lastAddTick = useRef(requestAdd);
  useEffect(() => {
    if (requestAdd === lastAddTick.current) return;
    lastAddTick.current = requestAdd;
    if (requestAdd < 1) return;
    setEditConfig(null);
    setShowModal(true);
  }, [requestAdd]);

  useEffect(() => {
    const loadProviderNames = async () => {
      try {
        const data = await apiService.get('/api/LLMProviderType/GetAll', {}, 'backend');
        const list = Array.isArray(data) ? data : (data?.data ?? data ?? []);
        const map: Record<string, string> = {};
        list.forEach((p: { id: string; name: string }) => { map[p.id] = p.name; });
        setProviderNames(map);
      } catch {
        setProviderNames({});
      }
    };
    loadProviderNames();
  }, []);

  const getDisplayName = (llm: LLMConfiguration) => {
    if (llm.additionalConfigJson) {
      try {
        const o = JSON.parse(llm.additionalConfigJson);
        if (o?.displayName) return o.displayName;
      } catch {}
    }
    return llm.modelName || llm.id;
  };

  const handleEdit = (llm: LLMConfiguration) => {
    setEditConfig(llm);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditConfig(null);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await llmConfigurationService.delete(deleteId);
      await fetchList();
      setDeleteId(null);
    } catch {
      setListError('Failed to delete configuration.');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActiveConfirm = async () => {
    if (!toggleStatusConfig) return;
    const llm = toggleStatusConfig;
    setTogglingId(llm.id);
    setToggleStatusConfig(null);
    try {
      await llmConfigurationService.setActive(llm.id, !llm.isActive);
      await fetchList();
    } catch {
      setListError('Failed to update status.');
    } finally {
      setTogglingId(null);
    }
  };

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';

  return (
    <div>
      {listError ? <p className="mb-3 text-sm text-red-400">{listError}</p> : null}

      {loading ? (
        <div className={`text-center py-10 text-sm ${muted}`}>Loading…</div>
      ) : llmsList.length === 0 ? (
        <div className={`text-center py-10 text-sm ${muted}`}>No LLM configurations yet. Add one to get started.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {llmsList.map((llm) => {
            const provider = providerNames[llm.provider] ?? llm.provider;
            const details = [
              provider,
              llm.modelName,
              llm.temperature != null ? `temp ${llm.temperature}` : null,
              llm.createdAt ? new Date(llm.createdAt).toLocaleDateString() : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <VaultEntityCard
                key={llm.id}
                isLight={isLight}
                title={getDisplayName(llm)}
                description={details || 'No description provided.'}
                tag={togglingId === llm.id ? '…' : llm.isActive ? 'Active' : 'Inactive'}
                tagTitle={llm.isActive ? 'Set inactive' : 'Set active'}
                icon={Brain}
                iconClass={isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'}
                inactive={!llm.isActive}
                onOpen={() => handleEdit(llm)}
                onEdit={() => handleEdit(llm)}
                onPermissions={() => setAssignmentModal(true)}
                onDelete={() => setDeleteId(llm.id)}
                onTagClick={togglingId === llm.id ? undefined : () => setToggleStatusConfig(llm)}
              />
            );
          })}
        </div>
      )}

      {showModal && (
        <AddLLMConfigModal
          onClose={handleCloseModal}
          onSubmit={fetchList}
          initialData={editConfig}
        />
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">Delete configuration?</h3>
            <p className="text-gray-400 text-sm mb-4">This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="text-gray-400 hover:text-white text-sm" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toggleStatusConfig && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">
              {toggleStatusConfig.isActive ? 'Set configuration to inactive?' : 'Set configuration to active?'}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              {toggleStatusConfig.isActive
                ? 'This configuration will be disabled and will not be available for use until you activate it again.'
                : 'This configuration will be enabled and available for use.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setToggleStatusConfig(null)}
                className="text-gray-400 hover:text-white text-sm"
                disabled={togglingId === toggleStatusConfig.id}
              >
                Cancel
              </button>
              <button
                onClick={handleToggleActiveConfirm}
                className={toggleStatusConfig.isActive ? 'bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50' : 'bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50'}
                disabled={togglingId === toggleStatusConfig.id}
              >
                {togglingId === toggleStatusConfig.id ? 'Updating…' : toggleStatusConfig.isActive ? 'Yes, set to Inactive' : 'Yes, set to Active'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignmentModal && (
        <PermissionsAssignmentModal
          onClose={() => setAssignmentModal(false)}
          onSave={() => {}}
          entityType="llm-configuration"
          entityName="LLM Configuration"
        />
      )}
    </div>
  );
};

export default LLMs;
